/**
 * Email Verification Page
 * Handles agent and student email verification after registration
 */
import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { authAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { CheckCircle, XCircle, Loader2, Mail, ArrowRight } from 'lucide-react';

const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error'
  const [errorMessage, setErrorMessage] = useState('');
  const [userData, setUserData] = useState(null);
  const [verificationType, setVerificationType] = useState('agent');

  const token = searchParams.get('token');
  const type = searchParams.get('type') || 'agent'; // 'agent' or 'student'

  useEffect(() => {
    setVerificationType(type);
    
    if (!token) {
      setStatus('error');
      setErrorMessage('Invalid verification link. No token provided.');
      return;
    }

    const verifyEmail = async () => {
      try {
        let response;
        if (type === 'student') {
          // Student verification
          response = await authAPI.verifyStudentEmail(token);
          setStatus('success');
          setUserData({ email: response.data.email });
        } else {
          // Agent verification
          response = await authAPI.verifyAgentEmail(token);
          setStatus('success');
          setUserData(response.data.user);
          
          // Auto-login the user (only for agents - students need to login manually)
          if (response.data.access_token) {
            localStorage.setItem('token', response.data.access_token);
            localStorage.setItem('user', JSON.stringify(response.data.user));
          }
        }
      } catch (err) {
        setStatus('error');
        setErrorMessage(err.response?.data?.detail || 'Verification failed. The link may have expired.');
      }
    };

    verifyEmail();
  }, [token, type]);

  const handleContinue = () => {
    if (verificationType === 'student') {
      navigate('/login');
    } else if (userData && localStorage.getItem('token')) {
      navigate('/agent');
    } else {
      navigate('/login');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 shadow-xl">
        <CardContent className="p-8">
          {status === 'verifying' && (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 mx-auto bg-teal-100 dark:bg-teal-900/30 rounded-full flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-white">Verifying Your Email</h2>
              <p className="text-gray-500 dark:text-gray-400">Please wait while we verify your email address...</p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 mx-auto bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-white">Email Verified!</h2>
              <p className="text-gray-500 dark:text-gray-400">
                Your email has been verified successfully. Your agent account is now active and pending approval.
              </p>
              
              {userData && (
                <div className="bg-gray-50 dark:bg-slate-700 rounded-lg p-4 text-left mt-4">
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    <strong>Welcome, {userData.full_name}!</strong>
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Your account is pending admin approval. You'll receive an email once approved.
                  </p>
                </div>
              )}

              <Button 
                onClick={handleContinue}
                className="w-full bg-teal-600 hover:bg-teal-700 text-white font-semibold mt-4"
              >
                Continue to Dashboard
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 mx-auto bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-white">Verification Failed</h2>
              <p className="text-gray-500 dark:text-gray-400">{errorMessage}</p>
              
              <div className="space-y-3 mt-6">
                <Link to="/agent-registration">
                  <Button className="w-full bg-teal-600 hover:bg-teal-700 text-white font-semibold">
                    <Mail className="w-4 h-4 mr-2" />
                    Register Again
                  </Button>
                </Link>
                <Link to="/login">
                  <Button variant="outline" className="w-full border-gray-300 dark:border-slate-600">
                    Back to Login
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifyEmailPage;
